/**
 * Thin-instance track renderer. One master mesh + one material per cell kind;
 * the visible window (WINDOW_ROWS_BEHIND..WINDOW_ROWS_AHEAD around the ship)
 * is rebuilt into preallocated matrix buffers whenever the ship crosses a row.
 * Zero per-frame allocation; draw calls = number of masters in view (< 25).
 *
 * R2-M4 additions: procedural panel-grid on floors (one 128 px canvas texture,
 * additive over the base emissive), a white-hot core strip inside the edge
 * neon, tunnel dressing (entrance/exit portals, interior ribs carrying a
 * 3-phase beat-traveling light wave, an additive lattice shell), and roadside
 * deco (pylons pulsing on the half-beat + far ring structures). Deco and the
 * shell are dropped on the Low tier; ribs and portals survive everywhere.
 *
 * One TrackRenderer per loaded level (palette baked into materials) —
 * dispose() before building the next.
 */
import { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { CreateTorus } from "@babylonjs/core/Meshes/Builders/torusBuilder";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Constants } from "@babylonjs/core/Engines/constants";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import "@babylonjs/core/Meshes/thinInstanceMesh";
import {
  CELL_WIDTH,
  ROW_DEPTH,
  LANES,
  laneCenterX,
  BLOCK_UNIT_HEIGHT,
  TUNNEL_ROOF_HEIGHT,
  WINDOW_ROWS_AHEAD,
  WINDOW_ROWS_BEHIND,
  DECO_PYLON_EVERY_ROWS,
  DECO_PYLON_X,
  DECO_RING_EVERY_ROWS,
  DECO_RING_X,
  TUNNEL_RIB_EVERY_ROWS,
  BEAT_PULSE_DECAY,
  type WorldPalette,
} from "../config/tuning";
import type { ParsedLevel } from "../track/types";
import { makeFlatMaterial, excludeFromGlow, unexcludeFromGlow } from "./scene";

const MAX_ROWS = WINDOW_ROWS_AHEAD + WINDOW_ROWS_BEHIND + 1;
const MAX_INSTANCES = MAX_ROWS * LANES;

type MasterKind =
  | "floor"
  | "block"
  | "hazard"
  | "tunnelRoof"
  | "checkpoint"
  | "finish"
  | "neonEdge"
  | "neonCore"
  | "boost"
  | "sticky"
  | "ice"
  | "rampUp"
  | "rampDown"
  | "rib0"
  | "rib1"
  | "rib2"
  | "shell"
  | "pylon"
  | "bigRing";

/** Unit ramp wedge: rises 0→1 across the row depth (z −0.5 → +0.5). */
function makeWedgeMesh(scene: Scene, name: string, down: boolean): Mesh {
  const mesh = new Mesh(name, scene);
  const zs = down ? -0.5 : 0.5; // high edge sits at the exit (or entry for down)
  const positions = [
    -0.5, 0, -0.5,  0.5, 0, -0.5,  0.5, 0, 0.5,  -0.5, 0, 0.5, // base
    -0.5, 1, zs,  0.5, 1, zs, // top edge over the high side
  ];
  const indices = down
    ? [0, 4, 5, 0, 5, 1, 1, 5, 2, 0, 3, 4, 3, 5, 4, 3, 2, 5]
    : [3, 5, 4, 3, 2, 5, 2, 1, 5, 0, 4, 1, 1, 4, 5, 0, 3, 4];
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  const data = new VertexData();
  data.positions = positions;
  data.indices = indices;
  data.normals = normals;
  data.applyToMesh(mesh);
  return mesh;
}

/** Torus baked to face down +z (a standing ring) so instances stay scale+translate. */
function makeFacingTorus(scene: Scene, name: string, thickness: number): Mesh {
  const mesh = CreateTorus(name, { diameter: 1, thickness, tessellation: 40 }, scene);
  mesh.rotation.x = Math.PI / 2;
  mesh.bakeCurrentTransformIntoVertices();
  return mesh;
}

/** Panel-grid overlay (§6.2): black canvas + faint lines. Emissive textures
 *  ADD to emissiveColor in Babylon's default shader, so this composes with
 *  the palette color, BeatFx modulation, and the Low-tier emissive boost. */
function makePanelTexture(scene: Scene): DynamicTexture {
  const size = 128;
  const tex = new DynamicTexture("panel-grid", size, scene, true);
  const ctx = tex.getContext();
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, size, size);
  ctx.strokeStyle = "#20242E"; // ≈4 % lift on the lines
  ctx.lineWidth = 2;
  for (let i = 0; i <= size; i += 32) {
    ctx.beginPath();
    ctx.moveTo(i + 0.5, 0);
    ctx.lineTo(i + 0.5, size);
    ctx.moveTo(0, i + 0.5);
    ctx.lineTo(size, i + 0.5);
    ctx.stroke();
  }
  tex.update(false);
  return tex;
}

interface Master {
  mesh: Mesh;
  buffer: Float32Array;
  count: number;
}

interface Portal {
  x: number;
  y: number;
  z: number;
  radius: number;
}

export class TrackRenderer {
  private masters: Record<MasterKind, Master>;
  private bitMeshes: Mesh[] = [];
  private panelTex: DynamicTexture;
  private lastCenterRow = -9999;
  /** Rows drawn ahead — quality-tier dependent, ≤ WINDOW_ROWS_AHEAD. */
  rowsAhead = WINDOW_ROWS_AHEAD;
  /** Roadside deco + tunnel shell (Med/High). Toggled by applyQuality. */
  private decorate: boolean;
  // portals are level-persistent (not windowed): rebuilt matrices each frame
  // for the slow counter-rotation, from this preallocated list
  private portals: Portal[] = [];
  private portalAngle = 0;
  private ribBase: Color3;
  private pylonBase: Color3;

  constructor(
    private readonly scene: Scene,
    private readonly level: ParsedLevel,
    private readonly palette: WorldPalette,
    rowsAhead = WINDOW_ROWS_AHEAD,
    decorate = true,
  ) {
    this.rowsAhead = Math.min(rowsAhead, WINDOW_ROWS_AHEAD);
    this.decorate = decorate;
    this.panelTex = makePanelTexture(scene);
    this.masters = {
      floor: this.makeMaster("floor", palette.road, 2.2),
      block: this.makeMaster("block", palette.neon, 0.55),
      hazard: this.makeMaster("hazard", palette.hazard, 1.0),
      tunnelRoof: this.makeMaster("tunnelRoof", palette.neon, 0.25),
      checkpoint: this.makeMaster("checkpoint", palette.accent, 0.8),
      finish: this.makeMaster("finish", palette.bit, 1.0),
      neonEdge: this.makeMaster("neonEdge", palette.neon, 1.0),
      // 1-px-white-core read (§6.2): a hotter, thinner strip inside the rail
      neonCore: this.makeMaster("neonCore", "#FFFFFF", 1.15),
      boost: this.makeMaster("boost", palette.accent, 1.3),
      sticky: this.makeMaster("sticky", palette.neon, 0.28),
      // ice is a global surface tone (like hazard red / bit white)
      ice: this.makeMaster("ice", "#BFE9F2", 0.55),
      rampUp: this.makeMaster("rampUp", palette.neon, 0.5, makeWedgeMesh(this.scene, "wedge-up", false)),
      rampDown: this.makeMaster("rampDown", palette.neon, 0.5, makeWedgeMesh(this.scene, "wedge-down", true)),
      rib0: this.makeMaster("rib0", palette.neon, 0.6),
      rib1: this.makeMaster("rib1", palette.neon, 0.6),
      rib2: this.makeMaster("rib2", palette.neon, 0.6),
      shell: this.makeMaster("shell", palette.accent, 0.35),
      pylon: this.makeMaster("pylon", palette.accent, 0.7),
      bigRing: this.makeMaster("bigRing", palette.neon, 0.45, makeFacingTorus(this.scene, "big-ring", 0.05)),
    };
    // floors carry the panel grid; additive, so it survives palette + boost
    (this.masters.floor.mesh.material as StandardMaterial).emissiveTexture = this.panelTex;
    // shell: additive translucency — a glow veil, never an occluder. Built
    // from side/top SLABS only: transverse faces would stack additively down
    // the tunnel's length and flood the interior with bloom.
    const shellMat = this.masters.shell.mesh.material as StandardMaterial;
    shellMat.alpha = 0.15;
    shellMat.alphaMode = Constants.ALPHA_ADD;
    shellMat.backFaceCulling = false;
    excludeFromGlow(this.masters.shell.mesh); // a bloomed veil floods the view
    this.ribBase = (this.masters.rib0.mesh.material as StandardMaterial).emissiveColor.clone();
    this.pylonBase = (this.masters.pylon.mesh.material as StandardMaterial).emissiveColor.clone();
    this.buildBits();
    this.buildPortals();
    this.rebuild(0);
  }

  /** Materials BeatFx modulates per frame (edge neon, hazard flash, boost chevron loop). */
  get pulsedMaterials(): {
    neon: StandardMaterial;
    hazard: StandardMaterial;
    boost: StandardMaterial;
  } {
    return {
      neon: this.masters.neonEdge.mesh.material as StandardMaterial,
      hazard: this.masters.hazard.mesh.material as StandardMaterial,
      boost: this.masters.boost.mesh.material as StandardMaterial,
    };
  }

  setDecorate(on: boolean): void {
    if (this.decorate === on) return;
    this.decorate = on;
    const row = Math.max(0, this.lastCenterRow);
    this.lastCenterRow = -9999;
    this.update(row * ROW_DEPTH + ROW_DEPTH / 2);
  }

  private makeMaster(name: string, hex: string, brightness: number, baseMesh?: Mesh): Master {
    const mesh = baseMesh ?? CreateBox(`track-${name}`, { size: 1 }, this.scene);
    mesh.name = `track-${name}`;
    mesh.material = makeFlatMaterial(this.scene, `mat-${name}`, hex, brightness);
    mesh.alwaysSelectAsActiveMesh = true; // thin-instance bboxes span the window
    mesh.isPickable = false;
    const buffer = new Float32Array(MAX_INSTANCES * 16);
    mesh.thinInstanceSetBuffer("matrix", buffer, 16, false);
    mesh.thinInstanceCount = 0;
    return { mesh, buffer, count: 0 };
  }

  private buildBits(): void {
    for (const bit of this.level.bits) {
      const mesh = CreateBox(`bit-${bit.index}`, { size: 0.5 }, this.scene);
      mesh.material = makeFlatMaterial(this.scene, `mat-bit-${bit.index}`, this.palette.bit, 1.2);
      mesh.rotation.x = Math.PI / 4;
      mesh.rotation.z = Math.PI / 4;
      mesh.position.set(laneCenterX(bit.lane), 0.85, bit.row * ROW_DEPTH + ROW_DEPTH / 2);
      mesh.isPickable = false;
      this.bitMeshes.push(mesh);
    }
  }

  /** Scan tunnel runs once; portals mark every entrance and exit (§2.3).
   *  Double ring: palette accent outer + white-core inner, counter-rotating. */
  private portalMeshA!: Mesh;
  private portalMeshB!: Mesh;
  private portalBufA!: Float32Array;
  private portalBufB!: Float32Array;

  private buildPortals(): void {
    const roofedAt = (row: number): { has: boolean; x: number; halfW: number } => {
      const cells = this.level.cells[row];
      if (!cells) return { has: false, x: 0, halfW: 0 };
      let lo = -1;
      let hi = -1;
      for (let lane = 0; lane < LANES; lane++) {
        const k = cells[lane]!.kind;
        if (k === "tunnel" || k === "tunnelHole") {
          if (lo === -1) lo = lane;
          hi = lane;
        }
      }
      if (lo === -1) return { has: false, x: 0, halfW: 0 };
      return {
        has: true,
        x: (laneCenterX(lo) + laneCenterX(hi)) / 2,
        halfW: ((hi - lo + 1) * CELL_WIDTH) / 2,
      };
    };
    let inRun = false;
    for (let row = 0; row <= this.level.numRows; row++) {
      const info = roofedAt(row);
      if (info.has && !inRun) {
        inRun = true;
        this.portals.push({ x: info.x, y: TUNNEL_ROOF_HEIGHT / 2 + 0.15, z: row * ROW_DEPTH, radius: info.halfW + 0.7 });
      } else if (!info.has && inRun) {
        inRun = false;
        const prev = roofedAt(row - 1);
        this.portals.push({ x: prev.x, y: TUNNEL_ROOF_HEIGHT / 2 + 0.15, z: row * ROW_DEPTH, radius: prev.halfW + 0.7 });
      }
    }
    this.portalMeshA = makeFacingTorus(this.scene, "track-portalA", 0.06);
    this.portalMeshA.material = makeFlatMaterial(this.scene, "mat-portalA", this.palette.accent, 1.2);
    this.portalMeshB = makeFacingTorus(this.scene, "track-portalB", 0.028);
    this.portalMeshB.material = makeFlatMaterial(this.scene, "mat-portalB", "#FFFFFF", 1.0);
    this.portalBufA = new Float32Array(Math.max(1, this.portals.length) * 16);
    this.portalBufB = new Float32Array(Math.max(1, this.portals.length) * 16);
    for (const mesh of [this.portalMeshA, this.portalMeshB]) {
      mesh.alwaysSelectAsActiveMesh = true;
      mesh.isPickable = false;
    }
    this.portalMeshA.thinInstanceSetBuffer("matrix", this.portalBufA, 16, false);
    this.portalMeshB.thinInstanceSetBuffer("matrix", this.portalBufB, 16, false);
    this.portalMeshA.thinInstanceCount = this.portals.length;
    this.portalMeshB.thinInstanceCount = this.portals.length;
    this.portalMeshA.setEnabled(this.portals.length > 0);
    this.portalMeshB.setEnabled(this.portals.length > 0);
  }

  /** Write a scale+translate matrix into a master's buffer. */
  private push(master: Master, sx: number, sy: number, sz: number, tx: number, ty: number, tz: number): void {
    const o = master.count * 16;
    const b = master.buffer;
    b[o + 0] = sx;  b[o + 1] = 0;   b[o + 2] = 0;   b[o + 3] = 0;
    b[o + 4] = 0;   b[o + 5] = sy;  b[o + 6] = 0;   b[o + 7] = 0;
    b[o + 8] = 0;   b[o + 9] = 0;   b[o + 10] = sz; b[o + 11] = 0;
    b[o + 12] = tx; b[o + 13] = ty; b[o + 14] = tz; b[o + 15] = 1;
    master.count++;
  }

  /** Rebuild the visible window if the ship crossed into a new row. */
  update(shipZ: number): void {
    const centerRow = Math.floor(shipZ / ROW_DEPTH);
    if (centerRow === this.lastCenterRow) return;
    this.lastCenterRow = centerRow;
    this.rebuild(centerRow);
  }

  private rebuild(centerRow: number): void {
    for (const m of Object.values(this.masters)) m.count = 0;

    const first = Math.max(0, centerRow - WINDOW_ROWS_BEHIND);
    const last = Math.min(this.level.numRows - 1, centerRow + this.rowsAhead);

    for (let row = first; row <= last; row++) {
      const cells = this.level.cells[row]!;
      const z = row * ROW_DEPTH + ROW_DEPTH / 2;

      // Edge neon rails on the outermost non-empty cells (Pillar A canvas)
      let leftLane = -1;
      let rightLane = -1;
      for (let lane = 0; lane < LANES; lane++) {
        if (cells[lane]!.kind !== "empty") {
          if (leftLane === -1) leftLane = lane;
          rightLane = lane;
        }
      }
      if (leftLane !== -1) {
        const railY = 0.12;
        const lx = laneCenterX(leftLane) - CELL_WIDTH / 2 + 0.07;
        const rx = laneCenterX(rightLane) + CELL_WIDTH / 2 - 0.07;
        this.push(this.masters.neonEdge, 0.14, 0.24, ROW_DEPTH, lx, railY, z);
        this.push(this.masters.neonEdge, 0.14, 0.24, ROW_DEPTH, rx, railY, z);
        this.push(this.masters.neonCore, 0.05, 0.3, ROW_DEPTH, lx, railY, z);
        this.push(this.masters.neonCore, 0.05, 0.3, ROW_DEPTH, rx, railY, z);
      }

      // tunnel interior dressing: ribs every 2 rows (3-phase wave groups) +
      // the additive lattice shell (decorated tiers only)
      let roofLo = -1;
      let roofHi = -1;
      for (let lane = 0; lane < LANES; lane++) {
        const k = cells[lane]!.kind;
        if (k === "tunnel" || k === "tunnelHole") {
          if (roofLo === -1) roofLo = lane;
          roofHi = lane;
        }
      }
      if (roofLo !== -1) {
        const cx = (laneCenterX(roofLo) + laneCenterX(roofHi)) / 2;
        const w = (roofHi - roofLo + 1) * CELL_WIDTH;
        if (row % TUNNEL_RIB_EVERY_ROWS === 0) {
          const group = (Math.floor(row / TUNNEL_RIB_EVERY_ROWS) % 3) as 0 | 1 | 2;
          const rib = this.masters[`rib${group}` as MasterKind];
          this.push(rib, w, 0.1, 0.12, cx, TUNNEL_ROOF_HEIGHT - 0.06, z);
          this.push(rib, 0.1, TUNNEL_ROOF_HEIGHT, 0.12, cx - w / 2 + 0.05, TUNNEL_ROOF_HEIGHT / 2, z);
          this.push(rib, 0.1, TUNNEL_ROOF_HEIGHT, 0.12, cx + w / 2 - 0.05, TUNNEL_ROOF_HEIGHT / 2, z);
        }
        if (this.decorate) {
          const sh = TUNNEL_ROOF_HEIGHT + 0.7;
          this.push(this.masters.shell, 0.05, sh, ROW_DEPTH, cx - w / 2 - 0.35, sh / 2 - 0.35, z);
          this.push(this.masters.shell, 0.05, sh, ROW_DEPTH, cx + w / 2 + 0.35, sh / 2 - 0.35, z);
          this.push(this.masters.shell, w + 0.7, 0.05, ROW_DEPTH, cx, TUNNEL_ROOF_HEIGHT + 0.35, z);
        }
      }

      // roadside deco (§6.3): pylons on an 8-row cadence, far rings sparser
      if (this.decorate && leftLane !== -1) {
        if (row % DECO_PYLON_EVERY_ROWS === 0) {
          this.push(this.masters.pylon, 0.22, 5.5, 0.22, -DECO_PYLON_X, 2.2, z);
          this.push(this.masters.pylon, 0.22, 5.5, 0.22, DECO_PYLON_X, 2.2, z);
        }
        if (row % DECO_RING_EVERY_ROWS === 0) {
          const side = Math.floor(row / DECO_RING_EVERY_ROWS) % 2 === 0 ? -1 : 1;
          this.push(this.masters.bigRing, 26, 26, 26, side * DECO_RING_X, 7, z);
        }
      }

      for (let lane = 0; lane < LANES; lane++) {
        const cell = cells[lane]!;
        const x = laneCenterX(lane);
        const w = CELL_WIDTH * 0.97; // hairline seams read as a grid
        const d = ROW_DEPTH * 0.97;
        switch (cell.kind) {
          case "empty":
            break;
          case "block": {
            const h = cell.height * BLOCK_UNIT_HEIGHT;
            this.push(this.masters.floor, w, 0.3, d, x, -0.15, z);
            this.push(this.masters.block, CELL_WIDTH * 0.95, h, ROW_DEPTH * 0.95, x, h / 2, z);
            break;
          }
          case "hazard":
            this.push(this.masters.hazard, w, 0.5, d, x, 0.25, z);
            break;
          case "tunnel":
            this.push(this.masters.floor, w, 0.3, d, x, -0.15, z);
            this.push(this.masters.tunnelRoof, w, 0.3, d, x, TUNNEL_ROOF_HEIGHT + 0.15, z);
            break;
          case "tunnelHole": // U: roof, NO floor — the hole is the point
            this.push(this.masters.tunnelRoof, w, 0.3, d, x, TUNNEL_ROOF_HEIGHT + 0.15, z);
            break;
          case "ice":
            this.push(this.masters.ice, w, 0.3, d, x, -0.15, z);
            break;
          case "rampUp":
            this.push(this.masters.rampUp, w, 1, d, x, cell.rampBase ?? 0, z);
            break;
          case "rampDown":
            this.push(this.masters.rampDown, w, 1, d, x, (cell.rampBase ?? 1) - 1, z);
            break;
          case "checkpoint":
            this.push(this.masters.checkpoint, w, 0.3, d, x, -0.15, z);
            break;
          case "finish":
            this.push(this.masters.finish, w, 0.3, d, x, -0.15, z);
            break;
          case "boost":
            this.push(this.masters.floor, w, 0.3, d, x, -0.15, z);
            this.push(this.masters.boost, CELL_WIDTH * 0.7, 0.06, ROW_DEPTH * 0.8, x, 0.04, z);
            break;
          case "sticky":
            this.push(this.masters.floor, w, 0.3, d, x, -0.15, z);
            this.push(this.masters.sticky, CELL_WIDTH * 0.7, 0.06, ROW_DEPTH * 0.8, x, 0.04, z);
            break;
          default:
            // floor, bit (floor under the gem), ramps (deferred, D-006)
            this.push(this.masters.floor, w, 0.3, d, x, -0.15, z);
            break;
        }
      }
    }

    for (const m of Object.values(this.masters)) {
      m.mesh.thinInstanceCount = m.count;
      m.mesh.thinInstanceBufferUpdated("matrix");
      m.mesh.setEnabled(m.count > 0);
    }
  }

  /**
   * Per-frame visual-only animation: bit gems spin; portals counter-rotate
   * and kick as the ship passes; ribs carry the beat wave; pylons pulse on
   * the half-beat. Reduced motion: rotation stops, pulses become slow glows.
   */
  animate(dtSeconds: number, beat = -1, shipZ = 0, reduced = false): void {
    for (const mesh of this.bitMeshes) {
      if (mesh.isVisible) mesh.rotation.y += dtSeconds * 2.2;
    }

    // portals: persistent instances, matrices rewritten in place (no alloc)
    if (this.portals.length > 0) {
      if (!reduced) this.portalAngle += dtSeconds * 0.55;
      const write = (buf: Float32Array, i: number, p: Portal, scale: number, angle: number): void => {
        const o = i * 16;
        const c = Math.cos(angle) * scale;
        const s = Math.sin(angle) * scale;
        buf[o + 0] = c;  buf[o + 1] = s;  buf[o + 2] = 0;  buf[o + 3] = 0;
        buf[o + 4] = -s; buf[o + 5] = c;  buf[o + 6] = 0;  buf[o + 7] = 0;
        buf[o + 8] = 0;  buf[o + 9] = 0;  buf[o + 10] = scale; buf[o + 11] = 0;
        buf[o + 12] = p.x; buf[o + 13] = p.y; buf[o + 14] = p.z; buf[o + 15] = 1;
      };
      for (let i = 0; i < this.portals.length; i++) {
        const p = this.portals[i]!;
        const near = Math.max(0, 1 - Math.abs(shipZ - p.z) / 1.6);
        const kick = reduced ? 0 : near * 0.18;
        write(this.portalBufA, i, p, p.radius * 2 * (1 + kick), this.portalAngle);
        write(this.portalBufB, i, p, p.radius * 2 * 0.88 * (1 + kick), -this.portalAngle * 1.4);
      }
      this.portalMeshA.thinInstanceBufferUpdated("matrix");
      this.portalMeshB.thinInstanceBufferUpdated("matrix");
    }

    const phase = beat >= 0 ? beat - Math.floor(beat) : 1;
    if (reduced) {
      this.ribBase.scaleToRef(0.75, (this.masters.rib0.mesh.material as StandardMaterial).emissiveColor);
      this.ribBase.scaleToRef(0.75, (this.masters.rib1.mesh.material as StandardMaterial).emissiveColor);
      this.ribBase.scaleToRef(0.75, (this.masters.rib2.mesh.material as StandardMaterial).emissiveColor);
      this.pylonBase.scaleToRef(0.8, (this.masters.pylon.mesh.material as StandardMaterial).emissiveColor);
      return;
    }
    // 3-phase traveling wave: each rib group peaks a third of a beat apart,
    // so light visibly runs rib→rib→rib down the tunnel (≤2.5 Hz per group)
    for (let g = 0; g < 3; g++) {
      const gPhase = (phase + g / 3) % 1;
      const bright = 0.45 + 1.15 * Math.exp(-gPhase * BEAT_PULSE_DECAY);
      const mat = this.masters[`rib${g}` as MasterKind].mesh.material as StandardMaterial;
      this.ribBase.scaleToRef(bright, mat.emissiveColor);
    }
    // pylons mark the half-beat (the off-beat the drums live on)
    const halfPhase = (phase * 2) % 1;
    this.pylonBase.scaleToRef(
      0.55 + 0.75 * Math.exp(-halfPhase * BEAT_PULSE_DECAY),
      (this.masters.pylon.mesh.material as StandardMaterial).emissiveColor,
    );
  }

  setBitCollected(index: number): void {
    const mesh = this.bitMeshes[index];
    if (mesh) mesh.isVisible = false;
  }

  resetBits(): void {
    for (const mesh of this.bitMeshes) mesh.isVisible = true;
  }

  /** Force a window rebuild (retry/respawn teleports the ship). */
  reset(shipZ = 0): void {
    this.lastCenterRow = -9999;
    this.resetBits();
    this.update(shipZ);
  }

  /** Tear down meshes + materials before loading another level. */
  dispose(): void {
    unexcludeFromGlow(this.masters.shell.mesh);
    for (const m of Object.values(this.masters)) {
      m.mesh.material?.dispose();
      m.mesh.dispose();
    }
    this.portalMeshA.material?.dispose();
    this.portalMeshA.dispose();
    this.portalMeshB.material?.dispose();
    this.portalMeshB.dispose();
    this.panelTex.dispose();
    for (const mesh of this.bitMeshes) {
      mesh.material?.dispose();
      mesh.dispose();
    }
    this.bitMeshes.length = 0;
  }
}
